import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anon, object = signed-in
  const [mode, setMode] = useState(() => localStorage.getItem("mt_mode") || "paper");

  useEffect(() => {
    let cancelled = false;
    api
      .get("/auth/me")
      .then((r) => { if (!cancelled) setUser(r.data); })
      .catch(() => { if (!cancelled) setUser(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    localStorage.setItem("mt_mode", mode);
  }, [mode]);

  async function login(email, password) {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) localStorage.setItem("mt_token", data.access_token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      const status = e.response?.status;
      const detail = formatApiErrorDetail(e.response?.data?.detail);
      // Build the most useful possible message for the user
      let msg;
      if (detail) msg = detail;
      else if (status) msg = `Login failed (HTTP ${status})`;
      else msg = `Cannot reach server: ${e.message}. Check your network connection.`;
      return { ok: false, error: msg };
    }
  }

  async function register(name, email, password, acceptLegal = true) {
    try {
      const { data } = await api.post("/auth/register", { name, email, password, accept_legal: acceptLegal });
      if (data.access_token) localStorage.setItem("mt_token", data.access_token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }

  async function logout() {
    try {
      await api.post("/auth/logout");
    } catch {
      // ignore — backend may already have invalidated the session
    }
    localStorage.removeItem("mt_token");
    setUser(false);
  }

  // Memoize context value so consumers don't re-render on every parent render
  const value = useMemo(
    () => ({ user, setUser, login, register, logout, mode, setMode }),
    [user, mode]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
